import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from '@mui/material';
import { Fingerprint as FingerprintIcon } from '@mui/icons-material';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { useTranslation } from 'react-i18next';
import { passkeyRegisterOptions, passkeyRegisterVerify } from '../lib/api';
import { useToast } from '../lib/toast';

interface Props {
  open: boolean;
  onClose: () => void;
  onRegistered: () => void;
}

export function PasskeyRegisterPrompt({ open, onClose, onRegistered }: Props) {
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useToast();
  const [loading, setLoading] = useState(false);

  // If the browser does not support WebAuthn, do not render the dialog
  if (!browserSupportsWebAuthn()) {
    return null;
  }

  async function handleRegister(): Promise<void> {
    setLoading(true);
    try {
      const options = await passkeyRegisterOptions();
      const credential = await startRegistration({ optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON });
      const result = await passkeyRegisterVerify(credential as unknown as Record<string, unknown>);
      if (result.verified) {
        notifySuccess(t('passkey.registerSuccess'));
        onRegistered();
      } else {
        notifyError(t('passkey.registerError'));
      }
    } catch {
      notifyError(t('passkey.registerError'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(148, 163, 184, 0.16)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <FingerprintIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h6">{t('passkey.register')}</Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {t('passkey.registerPrompt')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          variant="text"
          color="inherit"
          onClick={onClose}
          disabled={loading}
          sx={{ color: 'text.secondary' }}
        >
          {t('passkey.registerSkip')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleRegister()}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FingerprintIcon />}
        >
          {t('passkey.register')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
